<template>
  <v-container fluid class="dark-background pa-md-8">
    <v-row class="mb-5 align-center">
      <v-col cols="12" md="6">
        <h1 class="white--text">COO Dashboard</h1>
        <p class="grey--text text--lighten-1">Übersicht aller ToDos für das ausgewählte Studio.</p>
        <v-select
          class="mt-4"
          dark
          outlined
          hide-details
          @change="handleStudioChange"
          :items="studios"
          label="Studio auswählen"
          v-model="selectedStudio"
          item-text="name"
          item-value="_id"></v-select>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-md-end justify-center">
        <v-btn x-large color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus-circle-outline</v-icon>
          Neues ToDo
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <h2 class="headline white--text">
          <v-icon color="white" class="mr-2">mdi-clipboard-list-outline</v-icon>
          Offene ToDos in {{ selectedStudioName }}
        </h2>
      </v-col>

      <v-col cols="12" md="4" v-for="cat in categories" :key="cat.value">
        <v-card dark class="fill-height">
          <v-card-title>
            <v-icon left :color="cat.color">{{ cat.icon }}</v-icon>
            {{ cat.text }} ({{ openTodosByCategory[cat.value].length }})
          </v-card-title>
          <v-list dark dense color="transparent" v-if="openTodosByCategory[cat.value].length > 0">
            <v-list-item-group>
              <v-list-item v-for="todo in openTodosByCategory[cat.value]" :key="todo._id" @click="openDetailDialog(todo)">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">{{ todo.title }}</v-list-item-title>
                  <v-list-item-subtitle class="d-flex align-center mt-1">
                    <v-chip x-small :color="getPriorityColor(todo.priority)" class="mr-2">{{ mapPriorityName(todo.priority) }}</v-chip>
                    <span>{{ formatDate(todo.createdAt) }}{{ todo.subcategory ? ` | ${todo.subcategory}` : "" }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          <v-card-text class="text-center grey--text" v-else>🎉 Keine offenen ToDos!</v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <h2 class="headline white--text mt-4">
          <v-icon color="white" class="mr-2">mdi-check-all</v-icon>
          Erledigte ToDos
        </h2>
        <v-card dark class="mt-2">
          <v-expansion-panels accordion flat dark v-if="groupedResolvedTodos.length > 0">
            <v-expansion-panel v-for="yearGroup in groupedResolvedTodos" :key="yearGroup.year">
              <v-expansion-panel-header class="font-weight-bold"> Jahr {{ yearGroup.year }} </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-expansion-panels accordion flat dark>
                  <v-expansion-panel v-for="monthGroup in yearGroup.months" :key="monthGroup.monthName">
                    <v-expansion-panel-header> {{ monthGroup.monthName }} ({{ monthGroup.todos.length }} ToDos) </v-expansion-panel-header>
                    <v-expansion-panel-content class="pa-0">
                      <v-list dense color="transparent">
                        <v-list-item v-for="todo in monthGroup.todos" :key="todo._id" @click="openDetailDialog(todo)">
                          <v-list-item-content>
                            <v-list-item-title>{{ todo.title }}</v-list-item-title>
                            <v-list-item-subtitle> Erledigt am: {{ formatDate(todo.resolvedAt) }} </v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-chip small :color="getCategoryColor(todo.category)" dark>{{ todo.category }}</v-chip>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-card-text v-else class="text-center grey--text py-4">Keine erledigten ToDos für dieses Studio gefunden.</v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="my-8"></v-divider>

    <v-row>
      <v-col cols="12">
        <h2 class="headline white--text">
          <v-icon color="white" class="mr-2">mdi-chart-bar</v-icon>
          Membercard Abschluss-Statistik
        </h2>
      </v-col>
      <v-col cols="12">
        <v-card dark>
          <v-row class="pa-4" align="center">
            <v-col cols="12" md="6">
              <v-select v-model="durationSelect" label="Zeitraum" outlined hide-details :items="durationItems"></v-select>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-btn outlined block @click="getStats(true)" class="mr-2">Seit Anfang Jahr</v-btn>
            </v-col>
          </v-row>

          <v-card-text>
            <div v-if="loadingStats" class="text-center pa-10">
              <v-progress-circular indeterminate size="64"></v-progress-circular>
              <p class="mt-4">Lade Statistikdaten...</p>
            </div>
            <div v-else>
              <apexchart type="line" height="400" :options="chartOptions" :series="chartSeries"></apexchart>

              <h3 class="headline white--text mt-8 mb-2">Mitarbeiter-Statistik in {{ selectedStudioName }}</h3>
              <v-data-table :headers="tableHeaders" :items="tableData" hide-default-footer :items-per-page="-1" dark class="elevation-2">
                <template v-slot:body.append>
                  <tr class="font-weight-bold" style="background-color: rgba(255, 255, 255, 0.05)">
                    <td>Total</td>
                    <td class="text-center">{{ tableTotals.activated }}</td>
                    <td class="text-center">{{ tableTotals.ongoing }}</td>
                    <td class="text-center">{{ tableTotals.accepted }}</td>
                    <td class="text-center">{{ tableTotals.declined }}</td>
                    <td></td>
                    <td class="text-center">{{ tableTotals.studioQuote }}</td>
                  </tr>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog overlay-opacity="0.9" v-model="todoDialog" max-width="600px" persistent>
      <v-card dark>
        <v-form ref="todoForm" v-model="formValid">
          <v-card-title class="primary white--text">
            <span class="headline">Neuen ToDo Eintrag erstellen</span>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-select v-model="editedTodo.category" :items="categories" label="Kategorie" outlined hide-details></v-select>
            <v-select
              class="mt-4"
              v-if="editedTodo.category === 'marketing'"
              v-model="editedTodo.subcategory"
              :items="[
                { text: 'Umbau', value: 'Umbau' },
                { text: 'Events', value: 'Events' },
                { text: 'News', value: 'News' },
                { text: 'Sonstiges', value: 'Sonstiges' },
              ]"
              label="Unterkategorie Marketing"
              outlined
              hide-details></v-select>
            <v-select
              class="mt-4"
              v-if="editedTodo.category === 'coo'"
              v-model="editedTodo.subcategory"
              :items="sortedCooSubcategories"
              label="Unterkategorie COO"
              outlined
              hide-details></v-select>
            <v-text-field class="mt-4" v-model="editedTodo.title" label="Titel" :rules="[(v) => !!v || 'Pflichtfeld']" outlined></v-text-field>
            <v-textarea v-model="editedTodo.description" label="Beschreibung" :rules="[(v) => !!v || 'Pflichtfeld']" outlined></v-textarea>
            <v-select
              v-model="editedTodo.priority"
              :items="['low', 'medium', 'high']"
              label="Priorität"
              :rules="[(v) => !!v || 'Pflichtfeld']"
              outlined>
              <template v-slot:selection="{ item }">
                <v-chip :color="getPriorityColor(item)" dark>{{ mapPriorityName(item) }}</v-chip>
              </template>
              <template v-slot:item="{ item }">
                <v-chip :color="getPriorityColor(item)" dark>{{ mapPriorityName(item) }}</v-chip>
              </template>
            </v-select>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="todoDialog = false">Abbrechen</v-btn>
            <v-btn color="primary" @click="saveTodo" :disabled="!formValid">Speichern</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog fullscreen v-model="detailDialog" transition="dialog-bottom-transition">
      <v-card v-if="currentTodo" dark>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="closeDetailDialog"> <v-icon>mdi-close</v-icon> </v-btn>
          <v-toolbar-title>ToDo Details</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text @click="toggleEditMode" v-if="!editMode"> <v-icon left>mdi-pencil</v-icon> Bearbeiten </v-btn>
        </v-toolbar>
        <v-card-text class="pa-md-8 pa-4">
          <div v-if="editMode">
            <v-text-field v-model="editedTodo.title" label="Titel" outlined></v-text-field>
            <v-textarea v-model="editedTodo.description" label="Beschreibung" outlined></v-textarea>
            <v-card-actions>
              <v-btn text @click="toggleEditMode">Abbrechen</v-btn>
              <v-btn color="primary" @click="saveEdit">Speichern</v-btn>
            </v-card-actions>
          </div>
          <div v-else>
            <h2 class="display-1 mb-2">{{ currentTodo.title }}</h2>
            <div class="d-flex flex-wrap mb-4">
              <v-chip class="mr-2 mb-2" :color="currentTodo.resolved ? 'green' : 'orange'">
                <v-icon left small>{{ currentTodo.resolved ? "mdi-check" : "mdi-clock-outline" }}</v-icon>
                {{ currentTodo.resolved ? "Erledigt" : "Offen" }}
              </v-chip>
              <v-chip class="mr-2 mb-2" :color="getCategoryColor(currentTodo.category)">
                <v-icon left small>mdi-shape-outline</v-icon> {{ currentTodo.category }}
                {{ currentTodo.subcategory ? `> ${currentTodo.subcategory}` : "" }}
              </v-chip>
              <v-chip class="mr-2 mb-2" :color="getPriorityColor(currentTodo.priority)">
                <v-icon left small>mdi-flag-variant-outline</v-icon> {{ mapPriorityName(currentTodo.priority) }}
              </v-chip>
            </div>
            <p>
              <strong>Erstellt von:</strong> {{ currentTodo.createdBy?.vorname }} {{ currentTodo.createdBy?.nachname }} am
              {{ formatDate(currentTodo.createdAt, true) }}
            </p>
            <div v-if="currentTodo.resolved">
              <strong>Erledigt von:</strong> {{ currentTodo.resolvedBy?.vorname }} {{ currentTodo.resolvedBy?.nachname }} am
              {{ formatDate(currentTodo.resolvedAt, true) }}
            </div>
            <v-divider class="my-4"></v-divider>
            <h3 class="headline">Beschreibung</h3>
            <p style="white-space: pre-wrap" class="mt-2 body-1">{{ currentTodo.description }}</p>
            <v-divider class="my-4"></v-divider>
            <h3 class="headline">Anhänge</h3>
            <v-row dense class="my-4">
              <v-col cols="12" md="6">
                <v-file-input ref="fileInput" v-model="selectedFile" style="display: none" @change="onFileSelected"></v-file-input>
                <v-btn block outlined @click="triggerFileUpload"><v-icon left>mdi-paperclip</v-icon> Datei anhängen</v-btn>
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <h3 class="headline d-flex justify-space-between align-center">
              Zugewiesene Benutzer
              <v-btn small icon class="primary" @click="showAssignment = !showAssignment"
                ><v-icon>{{ showAssignment ? "mdi-chevron-up" : "mdi-plus" }}</v-icon></v-btn
              >
            </h3>
            <v-expand-transition>
              <div v-show="showAssignment" class="mt-4">
                <user-search
                  style="width: 100%"
                  class="mx-auto mt-5"
                  ignoreRights
                  instructor
                  :key="assignedUserKey"
                  @update="updateNewAssignedUser"
                  label="Benutzer suchen"></user-search>
                <v-btn color="primary" @click="submitAssignment" :disabled="!newAssignedUser">Zuweisen</v-btn>
              </div>
            </v-expand-transition>
            <v-list color="transparent" dense>
              <v-list-item v-for="assignment in currentTodo.assignedUsers" :key="assignment._id">
                <v-list-item-content>
                  <v-list-item-title>{{ assignment.user.vorname }} {{ assignment.user.nachname }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon small @click="removeUser(assignment._id)"><v-icon color="red lighten-2">mdi-account-minus</v-icon></v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="!currentTodo.assignedUsers || currentTodo.assignedUsers.length === 0">
                <v-list-item-content>
                  <v-list-item-subtitle>Keine Benutzer zugewiesen.</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-divider class="my-4"></v-divider>
            <h3 class="headline">Kommentare & Status</h3>
            <v-textarea v-model="newComment" class="mt-4" label="Neuer Kommentar..." outlined></v-textarea>
            <div class="d-flex align-center justify-space-between">
              <v-switch v-model="isResolved" :label="isResolved ? 'Als erledigt markiert' : 'Als offen markiert'"></v-switch>
              <v-btn large color="primary" @click="submitComment">ToDo aktualisieren</v-btn>
            </div>
            <v-list color="transparent" dense class="mt-4">
              <v-list-item v-for="comment in sortedComments" :key="comment._id">
                <v-list-item-content>
                  <v-list-item-subtitle
                    >{{ comment.user.vorname }} {{ comment.user.nachname }} am {{ formatDate(comment.createdAt, true) }}</v-list-item-subtitle
                  >
                  <p class="body-1 white--text mb-0">{{ comment.comment }}</p>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import userSearch from "@/components/userSearch.vue"
import VueApexCharts from "vue-apexcharts"

export default {
  name: "CooDashboard",
  components: { userSearch, apexchart: VueApexCharts },
  data() {
    return {
      // ToDo Data
      studios: [],
      selectedStudio: null,
      openTodos: [],
      resolvedTodos: [],
      todoDialog: false,
      detailDialog: false,
      editMode: false,
      currentTodo: null,
      editedTodo: {},
      formValid: false,
      newComment: "",
      isResolved: false,
      newAssignedUser: null,
      assignedUserKey: 0,
      showAssignment: false,
      selectedFile: null,
      categories: [
        { text: "Techniker", value: "techniker", icon: "mdi-hard-hat", color: "blue lighten-2" },
        { text: "Marketing", value: "marketing", icon: "mdi-bullhorn-outline", color: "orange lighten-2" },
        { text: "COO", value: "coo", icon: "mdi-account-tie", color: "purple lighten-2" },
      ],

      // Statistik Data
      loadingStats: false,
      stats: [],
      durationSelect: "letzter 1 Monat",
      durationItems: Array.from({ length: 12 }, (_, i) => `letzte${i === 0 ? "r" : "n"} ${i + 1} Monat${i > 0 ? "e" : ""}`),
      tableHeaders: [
        { text: "Trainer", value: "trainer", sortable: true },
        { text: "Aktiviert", value: "activated", align: "center" },
        { text: "Laufend", value: "ongoing", align: "center" },
        { text: "Abgeschlossen", value: "accepted", align: "center" },
        { text: "Kein Abschluss", value: "declined", align: "center" },
        { text: "Quote Mitarbeiter", value: "quote", align: "center" },
        { text: "Quote Studio", value: "studioQuote", align: "center" },
      ],
    }
  },
  computed: {
    sortedCooSubcategories() {
      const subcategories = [
        { text: "Solarium", value: "Solarium" },
        { text: "Me-Scan", value: "Me-Scan" },
        { text: "Hydrojet", value: "Hydrojet" },
        { text: "Ausdauergeräte", value: "Ausdauergeräte" },
        { text: "Kraftgeräte", value: "Kraftgeräte" },
        { text: "Senso Pro", value: "Senso Pro" },
        { text: "Milon", value: "Milon" },
        { text: "Stretching", value: "Stretching" },
        { text: "Plate Loaded", value: "Plate Loaded" },
        { text: "Kabeltürme", value: "Kabeltürme" },
        { text: "Workout ", value: "Workout " },
        { text: "LH", value: "LH" },
        { text: "KH", value: "KH" },
        { text: "Racks", value: "Racks" },
        { text: "MP", value: "MP" },
        { text: "Functional", value: "Functional" },
        { text: "Group Fitness", value: "Group Fitness" },
        { text: "Wellness", value: "Wellness" },
        { text: "Getränkeanlage", value: "Getränkeanlage" },
        { text: "Power Plate", value: "Power Plate" },
        { text: "Kinderhort", value: "Kinderhort" },
        { text: "Pelvix Power", value: "Pelvix Power" },
        { text: "Shop", value: "Shop" },
        { text: "Abfall", value: "Abfall" },
        { text: "Allgemein", value: "Allgemein" },
        { text: "roody", value: "roody" },
        { text: "TAC", value: "TAC" },
        { text: "Schulungen", value: "Schulungen" },
      ]
      // Sortiere das Array alphabetisch basierend auf dem 'text'-Feld
      return subcategories.sort((a, b) => a.text.localeCompare(b.text))
    },
    selectedStudioName() {
      if (!this.selectedStudio) return "..."
      const studio = this.studios.find((s) => s._id === this.selectedStudio)
      return studio ? studio.name : ""
    },
    openTodosByCategory() {
      const grouped = { techniker: [], marketing: [], coo: [] }
      this.openTodos.forEach((todo) => {
        if (grouped[todo.category]) {
          grouped[todo.category].push(todo)
        }
      })
      for (const cat in grouped) {
        grouped[cat].sort((a, b) => b.priorityValue - a.priorityValue)
      }
      return grouped
    },
    groupedResolvedTodos() {
      const groups = {}
      this.resolvedTodos.forEach((todo) => {
        const date = new Date(todo.resolvedAt || todo.createdAt)
        const year = date.getFullYear()
        const month = date.getMonth()
        if (!groups[year]) groups[year] = {}
        if (!groups[year][month]) groups[year][month] = []
        groups[year][month].push(todo)
      })
      return Object.keys(groups)
        .sort((a, b) => b - a)
        .map((year) => ({
          year,
          months: Object.keys(groups[year])
            .sort((a, b) => b - a)
            .map((month) => ({
              monthName: new Date(2020, month).toLocaleString("default", { month: "long" }),
              todos: groups[year][month].sort((a, b) => new Date(b.resolvedAt) - new Date(a.resolvedAt)),
            })),
        }))
    },
    sortedComments() {
      if (this.currentTodo && this.currentTodo.comments) {
        return [...this.currentTodo.comments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      }
      return []
    },
    chartSeries() {
      if (!this.stats.length) return []
      const sourceData = this.getAggregatedStudioData()
      const seriesData = { accepted: [], declined: [], ongoing: [] }

      for (const key in sourceData) {
        seriesData.accepted.push(sourceData[key].accepted)
        seriesData.declined.push(sourceData[key].declined)
        seriesData.ongoing.push(sourceData[key].ongoing)
      }

      return [
        { name: "Kein Abschluss", type: "column", data: seriesData.declined },
        { name: "Erfolgreich", type: "column", data: seriesData.accepted },
        { name: "Laufend", type: "column", data: seriesData.ongoing },
        { name: "Abschlüsse (Linie)", type: "line", data: seriesData.accepted },
      ]
    },
    chartOptions() {
      const sourceData = this.getAggregatedStudioData()
      const labels = Object.keys(sourceData)
      return {
        chart: { type: "line", height: 400, stacked: true, foreColor: "#fff" },
        colors: ["#d62828", "#f5b700", "#0077b6", "#4CAF50"],
        stroke: { width: [0, 0, 0, 4], curve: "smooth" },
        plotOptions: { bar: { columnWidth: "60%" } },
        fill: { opacity: [1, 1, 1, 1] },
        labels: labels,
        xaxis: {
          type: "category",
          labels: { style: { colors: "#fff" }, rotate: -45, rotateAlways: true, trim: false, hideOverlappingLabels: false },
        },
        yaxis: { title: { text: "Anzahl", style: { color: "#fff" } }, labels: { style: { colors: "#fff" } } },
        tooltip: { theme: "dark", fixed: { enabled: true } },
        grid: { borderColor: "#555" },
        dataLabels: {
          enabled: true,
          formatter: function (val, { seriesIndex }) {
            if (seriesIndex === 3) return val
            return ""
          },
          offsetY: -20,
          style: { fontSize: "12px", colors: ["#fff"] },
          background: { enabled: true, foreColor: "#000", padding: 4, borderRadius: 2, borderWidth: 1, borderColor: "#4CAF50", opacity: 0.7 },
        },
        legend: { position: "top", horizontalAlign: "left" },
      }
    },
    tableData() {
      if (!this.selectedStudio || !this.stats.length) return []
      const studioName = this.studios.find((s) => s._id === this.selectedStudio)?.name
      return this.stats.filter((item) => item.studio === studioName)
    },
    tableTotals() {
      const totals = this.tableData.reduce(
        (acc, item) => {
          acc.activated += item.activated
          acc.accepted += item.accepted
          acc.declined += item.declined
          acc.ongoing += item.ongoing
          return acc
        },
        { activated: 0, accepted: 0, declined: 0, ongoing: 0 }
      )

      if (this.tableData.length > 0) {
        totals.studioQuote = this.tableData[0].studioQuote
      } else {
        totals.studioQuote = "0.00%"
      }
      return totals
    },
  },
  watch: {
    durationSelect() {
      this.getStats()
    },
  },
  async created() {
    await this.fetchStudios()
    this.selectedStudio = userService.getStudio()?._id || (this.studios.length > 0 ? this.studios[0]._id : null)
    this.handleStudioChange()
  },
  methods: {
    async fetchStudios() {
      let studios = await api.fetchStudios()
      this.studios = studios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    handleStudioChange() {
      this.loadAllTodos()
      this.getStats()
    },
    async loadAllTodos() {
      if (!this.selectedStudio) return
      const open = api.toDoList.fetchAllCoo({ status: "open", studio: this.selectedStudio, forceStudio: true })
      const resolved = api.toDoList.fetchAllCoo({ status: "resolved", studio: this.selectedStudio, forceStudio: true })
      const [openData, resolvedData] = await Promise.all([open, resolved])
      this.openTodos = openData.map((todo) => ({ ...todo, priorityValue: this.getPriorityValue(todo.priority) }))
      this.resolvedTodos = resolvedData
    },
    openCreateDialog() {
      this.editedTodo = {
        _id: null,
        title: "",
        description: "",
        priority: "medium",
        category: "techniker",
        subcategory: null,
        studio: this.selectedStudio,
      }
      this.todoDialog = true
    },
    saveTodo() {
      if (!this.$refs.todoForm.validate()) return
      this.editedTodo.createdBy = userService.getUser()._id
      api.toDoList.create(this.editedTodo).then(() => {
        this.$toast.success("ToDo erfolgreich erstellt!")
        this.todoDialog = false
        this.loadAllTodos()
      })
    },
    async openDetailDialog(todo) {
      const [freshTodo] = await api.toDoList.fetchAllCoo({ _id: todo._id, forceStudio: true })
      if (freshTodo) {
        this.currentTodo = freshTodo
        this.isResolved = freshTodo.resolved
        this.detailDialog = true
      }
    },
    toggleEditMode() {
      this.editMode = !this.editMode
      if (this.editMode) {
        this.editedTodo = JSON.parse(JSON.stringify(this.currentTodo))
        this.editedTodo.studio = this.currentTodo.studio?._id
      }
    },
    async saveEdit() {
      try {
        await api.toDoList.update(this.editedTodo._id, this.editedTodo)
        this.$toast.success("ToDo erfolgreich aktualisiert!")
        this.editMode = false
        await this.refreshCurrentTodo()
        this.loadAllTodos()
      } catch (err) {
        this.$toast.error("Fehler: " + err)
      }
    },
    async refreshCurrentTodo() {
      if (!this.currentTodo) return
      const [reloadedTodo] = await api.toDoList.fetchAllCoo({ _id: this.currentTodo._id, forceStudio: true })
      if (reloadedTodo) {
        this.currentTodo = reloadedTodo
      }
    },
    async submitComment() {
      const payload = {
        comment: this.newComment,
        status: this.isResolved ? "resolved" : "opened",
      }
      try {
        await api.toDoList.addComment(this.currentTodo._id, payload)
        this.$toast.success("ToDo aktualisiert!")
        this.newComment = ""
        const wasResolved = this.currentTodo.resolved

        await this.loadAllTodos()

        if (this.isResolved !== wasResolved) {
          this.closeDetailDialog()
        } else {
          await this.refreshCurrentTodo()
        }
      } catch (err) {
        this.$toast.error("Fehler: " + err)
      }
    },
    async onFileSelected(file) {
      if (!file) return
      const formData = new FormData()
      formData.append("file", file)
      try {
        await api.toDoList.uploadAttachment(formData, this.currentTodo._id)
        this.$toast.success("Datei erfolgreich hochgeladen!")
        this.selectedFile = null
        await this.refreshCurrentTodo()
      } catch (err) {
        this.$toast.error("Upload fehlgeschlagen: " + err.response?.data?.message || err.message)
      }
    },
    triggerFileUpload() {
      this.$refs.fileInput.$el.querySelector("input").click()
    },
    updateNewAssignedUser(user) {
      this.newAssignedUser = user
    },
    async submitAssignment() {
      if (!this.newAssignedUser) return
      const payload = { assignment: { user: this.newAssignedUser, rights: "manage" } }
      try {
        await api.toDoList.assign(this.currentTodo._id, payload)
        this.$toast.success("Benutzer zugewiesen!")
        this.newAssignedUser = null
        this.assignedUserKey++
        this.showAssignment = false
        await this.refreshCurrentTodo()
        this.loadAllTodos()
      } catch (err) {
        this.$toast.error("Fehler: " + err)
      }
    },
    async removeUser(assignmentId) {
      try {
        await api.toDoList.removeAssignment(this.currentTodo._id, assignmentId)
        this.$toast.success("Zuweisung entfernt.")
        await this.refreshCurrentTodo()
        this.loadAllTodos()
      } catch (err) {
        this.$toast.error("Fehler: " + err)
      }
    },
    closeDetailDialog() {
      this.detailDialog = false
      this.editMode = false
      setTimeout(() => {
        this.currentTodo = null
      }, 300)
    },
    getAggregatedStudioData() {
      const aggregated = {}
      this.stats.forEach((item) => {
        if (!aggregated[item.studio]) {
          aggregated[item.studio] = { activated: 0, accepted: 0, declined: 0, ongoing: 0 }
        }
        aggregated[item.studio].activated += item.activated
        aggregated[item.studio].accepted += item.accepted
        aggregated[item.studio].declined += item.declined
        aggregated[item.studio].ongoing += item.ongoing
      })
      return aggregated
    },
    async getStats(startOfYear = false) {
      let amount = (this.durationItems.indexOf(this.durationSelect) + 1) * 30
      if (startOfYear) {
        const startDate = this.$moment().startOf("year")
        amount = this.$moment().diff(startDate, "days")
      }
      try {
        this.loadingStats = true
        const data = await api.fetchMembercardStatisticAdminCoo(amount)
        this.stats = data.stats
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Statistik")
      } finally {
        this.loadingStats = false
      }
    },
    formatDate(date, withTime = false) {
      return date ? this.$moment(date).format(withTime ? "DD.MM.YY HH:mm" : "DD.MM.YY") : ""
    },
    mapPriorityName(p) {
      return { high: "Hoch", medium: "Mittel", low: "Niedrig" }[p] || p
    },
    getPriorityValue(p) {
      return { high: 3, medium: 2, low: 1 }[p] || 0
    },
    getPriorityColor(p) {
      return { high: "red accent-2", medium: "orange darken-2", low: "green darken-1" }[p] || "grey"
    },
    getCategoryColor(c) {
      return { techniker: "blue lighten-2", marketing: "orange lighten-2", coo: "purple lighten-2" }[c] || "grey"
    },
  },
}
</script>

<style scoped>
.dark-background {
  background-color: #1e1e1e;
  min-height: 100vh;
}
.v-card {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background-color: #1e1e1e !important;
}
.v-list-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}
</style>
